class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.recommendationsTimer = null;
    this.recommendationIndex = 0;
    this.activeUtilityPanelName = '';
    this.fitmentObserver = null;
    this.fitmentDedupeTimer = null;
    this.fitmentObserverTarget = null;
    this.cartAddonsFrame = null;
    this.cartAddonsIdle = null;
    this.cartAddonsTimer = null;
    this.cartDrawerOpenTimer = null;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.onCartDrawerClick.bind(this));
    this.addEventListener('keydown', this.onCartDrawerKeydown.bind(this));
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.applySavedDiscountToCheckoutForms();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    window.clearTimeout(this.cartDrawerOpenTimer);
    this.cartDrawerOpenTimer = window.setTimeout(() => {
      this.classList.add('animate', 'active');
      this.deferCartDrawerAddons();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.hideExternalSupportButtons();
  }

  close() {
    window.clearTimeout(this.cartDrawerOpenTimer);
    this.classList.remove('active');
    if (this.cartAddonsFrame) window.cancelAnimationFrame(this.cartAddonsFrame);
    if (this.cartAddonsIdle && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(this.cartAddonsIdle);
    window.clearTimeout(this.cartAddonsTimer);
    window.clearInterval(this.recommendationsTimer);
    this.disconnectFitmentObserver();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.showExternalSupportButtons();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.product_id || (parsedState.product && parsedState.product.id) || '';
    this.getSectionsToRender().forEach((section) => {
      if (!parsedState.sections || !parsedState.sections[section.id]) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : html;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initCartDrawerAddons() {
    try {
      this.observeFitmentDuplicates();
      this.updateFitmentStatuses();
      this.setupUtilityPanels();
      this.applySavedDiscountToCheckoutForms();
      this.deferRecommendationWork();
    } catch (error) {
      console.warn('Cart drawer addons skipped:', error);
    }
  }

  deferCartDrawerAddons() {
    if (this.cartAddonsFrame) window.cancelAnimationFrame(this.cartAddonsFrame);

    this.cartAddonsFrame = window.requestAnimationFrame(() => {
      this.cartAddonsFrame = null;
      this.initCartDrawerAddons();
    });
  }

  deferRecommendationWork() {
    const run = () => {
      this.cartAddonsIdle = null;
      this.cartAddonsTimer = null;
      this.loadRecommendations();
      this.setupRecommendationsSlider();
    };

    if (this.cartAddonsIdle && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(this.cartAddonsIdle);
    window.clearTimeout(this.cartAddonsTimer);

    if (typeof window.requestIdleCallback === 'function') {
      this.cartAddonsIdle = window.requestIdleCallback(run, { timeout: 700 });
    } else {
      this.cartAddonsTimer = window.setTimeout(run, 240);
    }
  }

  onCartDrawerClick(event) {
    const utilityTrigger = event.target.closest('[data-cart-utility-trigger]');
    if (utilityTrigger) {
      event.preventDefault();
      this.toggleUtilityPanel(utilityTrigger.dataset.cartUtilityTrigger, utilityTrigger);
      return;
    }

    const discountApply = event.target.closest('[data-cart-discount-apply]');
    if (discountApply) {
      event.preventDefault();
      this.applyDiscountFromField();
      return;
    }

    const shippingApply = event.target.closest('[data-cart-shipping-apply]');
    if (shippingApply) {
      event.preventDefault();
      this.renderShippingEstimate(this.getShippingInputValue(), true);
      return;
    }

    const addButton = event.target.closest('[data-recommendation-add]');
    if (addButton) {
      event.preventDefault();
      this.addRecommendationToCart(addButton);
      return;
    }

    const previousButton = event.target.closest('[data-recommendations-prev]');
    if (previousButton) {
      event.preventDefault();
      this.moveRecommendationSlide(-1);
      return;
    }

    const nextButton = event.target.closest('[data-recommendations-next]');
    if (nextButton) {
      event.preventDefault();
      this.moveRecommendationSlide(1);
    }
  }

  onCartDrawerKeydown(event) {
    if (event.key !== 'Enter') return;

    if (event.target.closest('[data-cart-discount-field]')) {
      event.preventDefault();
      this.applyDiscountFromField();
      return;
    }

    if (event.target.closest('[data-cart-shipping-zip]')) {
      event.preventDefault();
      this.renderShippingEstimate(this.getShippingInputValue(), true);
    }
  }

  updateFitmentStatuses() {
    const statusNodes = this.querySelectorAll('[data-cart-fitment-status]');

    statusNodes.forEach((statusNode) => {
      const productId = statusNode.dataset.productId;
      const cartItem = statusNode.closest('.cart-item');
      const savedState = this.getSavedFitmentState(productId);
      const externalState = this.getExternalFitmentState(cartItem) || this.getMatchingCartPageFitmentState(statusNode);
      const storedStatus = this.normalizeFitmentStatus(statusNode.dataset.fitmentStatus);
      const savedStatus = this.normalizeFitmentStatus(savedState && savedState.status);
      const externalStatus = this.normalizeFitmentStatus(externalState && externalState.status);
      let status = externalStatus !== 'pending' ? externalStatus : savedStatus !== 'pending' ? savedStatus : storedStatus;
      const savedVehicle = savedState && savedState.vehicle ? savedState.vehicle : '';
      const externalVehicle = externalState && externalState.vehicle ? externalState.vehicle : '';
      const vehicle = status === storedStatus ? statusNode.dataset.fitmentVehicle || savedVehicle || externalVehicle : savedVehicle || externalVehicle;

      this.setFitmentStatus(statusNode, status, vehicle);
    });

    this.dedupeExternalFitmentMessages();
  }

  observeFitmentDuplicates() {
    if (typeof MutationObserver === 'undefined') return;

    const target = this.querySelector('.drawer__inner') || this;
    if (this.fitmentObserver && this.fitmentObserverTarget === target) return;
    if (this.fitmentObserver) this.fitmentObserver.disconnect();

    this.fitmentObserver = new MutationObserver(() => {
      window.clearTimeout(this.fitmentDedupeTimer);
      this.fitmentDedupeTimer = window.setTimeout(() => this.updateFitmentStatuses(), 80);
    });
    this.fitmentObserver.observe(target, {
      childList: true,
      subtree: true,
    });
    this.fitmentObserverTarget = target;
  }

  disconnectFitmentObserver() {
    window.clearTimeout(this.fitmentDedupeTimer);
    if (this.fitmentObserver) this.fitmentObserver.disconnect();
    this.fitmentObserver = null;
    this.fitmentObserverTarget = null;
  }

  getExternalFitmentState(cartItem) {
    if (!cartItem) return null;

    const details = cartItem.querySelector('.cart-item__details') || cartItem;
    const clone = details.cloneNode(true);
    clone.querySelectorAll('[data-cart-fitment-status], script, style').forEach((node) => node.remove());
    const text = (clone.textContent || '').replace(/\s+/g, ' ').trim();

    if (this.isNotFitText(text)) return { status: 'not_fit', vehicle: '' };
    if (this.isVerifiedText(text)) return { status: 'verified', vehicle: '' };

    return null;
  }

  getMatchingCartPageFitmentState(statusNode) {
    const pageItems = Array.from(document.querySelectorAll('cart-items .cart-item'));
    if (!pageItems.length) return null;

    const productId = statusNode.dataset.productId || '';
    const productHandle = statusNode.dataset.productHandle || '';
    const drawerItem = statusNode.closest('.cart-item');
    const drawerPath = this.normalizeProductPath(drawerItem && drawerItem.querySelector('.cart-item__name')?.getAttribute('href'));

    const matchingItem = pageItems.find((item) => {
      if (productId && item.dataset.cartProductId === productId) return true;
      if (productHandle && item.dataset.cartProductHandle === productHandle) return true;

      const itemPath = this.normalizeProductPath(item.querySelector('.cart-item__name')?.getAttribute('href'));
      return drawerPath && itemPath && drawerPath === itemPath;
    });

    return this.getExternalFitmentState(matchingItem);
  }

  normalizeProductPath(value) {
    if (!value) return '';

    try {
      return new URL(value, window.location.origin).pathname.replace(/\/$/, '');
    } catch (error) {
      return String(value).split('?')[0].replace(/\/$/, '');
    }
  }

  dedupeExternalFitmentMessages() {
    this.querySelectorAll('.cart-item__details').forEach((details) => {
      if (!details.querySelector('[data-cart-fitment-status]')) return;

      this.hideKnownExternalFitmentNodes(details);
      this.hideTextFitmentDuplicates(details);
      this.hideDuplicateOwnFitmentNodes(details);
    });
  }

  hideKnownExternalFitmentNodes(details) {
    const selectors = [
      '.vfitz-cart',
      '[class*="vfitz"]',
      '[id*="vfitz"]',
      '#valid',
      '[id^="valid"]',
      '[class*="fitment"]:not(.cart-fitment-status):not(.cart-fitment-status__dot):not(.cart-fitment-status__label)',
      '[class*="vehicle"]:not(.cart-fitment-status):not(.cart-fitment-status__dot):not(.cart-fitment-status__label)',
    ];

    details.querySelectorAll(selectors.join(',')).forEach((node) => {
      if (node.closest('[data-cart-fitment-status]')) return;
      this.hideFitmentDuplicate(node);
    });
  }

  hideTextFitmentDuplicates(details) {
    const duplicates = new Set();
    const walker = document.createTreeWalker(details, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement;
        if (!parent || parent.closest('[data-cart-fitment-status]')) return NodeFilter.FILTER_REJECT;
        const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
        return this.isFitmentText(text) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    while (walker.nextNode()) {
      const duplicate = this.getFitmentDuplicateContainer(walker.currentNode.parentElement, details);
      if (duplicate) duplicates.add(duplicate);
    }

    duplicates.forEach((node) => this.hideFitmentDuplicate(node));
  }

  hideDuplicateOwnFitmentNodes(details) {
    const ownStatuses = details.querySelectorAll('[data-cart-fitment-status]');
    ownStatuses.forEach((node, index) => {
      if (index > 0) this.hideFitmentDuplicate(node);
    });
  }

  getFitmentDuplicateContainer(element, boundary) {
    let node = element;
    let candidate = element;

    while (node && node !== boundary) {
      const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (this.isFitmentText(text) && text.length <= 180 && !/\$\s*\d/.test(text)) candidate = node;
      node = node.parentElement;
    }

    return candidate && candidate !== boundary ? candidate : element;
  }

  hideFitmentDuplicate(node) {
    if (!node) return;
    if (!node.matches('[data-cart-fitment-status]') && node.closest('[data-cart-fitment-status]')) return;
    node.classList.add('soillar-fitment-duplicate-hidden');
    node.setAttribute('aria-hidden', 'true');
  }

  isFitmentText(text) {
    return this.isNotFitText(text) || this.isVerifiedText(text) || /verify\s*your\s*vehicle/i.test(text || '');
  }

  isNotFitText(text) {
    return /(not\s*match\s*the\s*vehicle|not\s*fit\s*your\s*vehicle|does\s*not\s*fit|doesn't\s*fit|not\s*compatible|验证失败|不适配|不匹配)/i.test(text || '');
  }

  isVerifiedText(text) {
    return /(vehicle\s*verified|verified\s*vehicle|fits\s*your\s*vehicle|fit\s*your\s*vehicle|验证通过|适配成功)/i.test(text || '');
  }

  getSavedFitmentState(productId) {
    if (!productId) return null;

    try {
      const rawState = window.localStorage.getItem(`soillar-fitment-state-${productId}`);
      if (!rawState) return null;

      const state = JSON.parse(rawState);
      const maxAge = 1000 * 60 * 60 * 24 * 14;
      if (state.updatedAt && Date.now() - state.updatedAt > maxAge) return null;

      return state;
    } catch (error) {
      return null;
    }
  }

  normalizeFitmentStatus(status) {
    if (status === 'verified' || status === 'not_fit') return status;
    return 'pending';
  }

  setFitmentStatus(statusNode, status, vehicle = '') {
    const label = statusNode.querySelector('.cart-fitment-status__label');
    const className = status === 'not_fit' ? 'not-fit' : status;

    statusNode.classList.remove(
      'cart-fitment-status--verified',
      'cart-fitment-status--not-fit',
      'cart-fitment-status--pending'
    );
    statusNode.classList.add(`cart-fitment-status--${className}`);
    statusNode.dataset.fitmentStatus = status;
    statusNode.dataset.fitmentVehicle = vehicle;

    if (!label) return;

    if (status === 'verified') {
      label.textContent = 'Verified';
    } else if (status === 'not_fit') {
      label.textContent = 'Not Fit your Vehicle';
    } else {
      label.textContent = 'Verify Your Vehicle';
    }
  }

  loadRecommendations() {
    const container = this.querySelector('[data-cart-drawer-recommendations]');
    const list = container ? container.querySelector('[data-recommendations-list]') : null;
    if (!container || !list) return;

    const productId = this.getRecommendationProductId(container);
    if (!productId || this.classList.contains('is-empty')) {
      this.hideRecommendations(container);
      return;
    }

    if (container.dataset.loadedProductId === String(productId) && container.classList.contains('is-ready')) return;
    if (container.dataset.loadingProductId === String(productId)) return;

    container.dataset.loadingProductId = String(productId);
    container.classList.remove('is-ready');
    container.setAttribute('aria-hidden', 'true');
    list.innerHTML = '<div class="cart-drawer__recommendations-loading">Loading...</div>';

    fetch(`/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=6`)
      .then((response) => response.json())
      .then((data) => {
        const products = Array.isArray(data.products) ? data.products : [];

        if (!products.length) {
          this.hideRecommendations(container);
          return;
        }

        list.innerHTML = products.map((product) => this.renderRecommendation(product)).join('');
        container.dataset.loadedProductId = String(productId);
        container.dataset.loadingProductId = '';
        container.classList.add('is-ready');
        container.setAttribute('aria-hidden', 'false');
        this.recommendationIndex = 0;
        this.setupRecommendationsSlider();
      })
      .catch(() => {
        this.hideRecommendations(container);
      });
  }

  getRecommendationProductId(container) {
    const firstCartItem = this.querySelector('[data-cart-product-id]');
    return this.productId || container.dataset.productId || (firstCartItem && firstCartItem.dataset.cartProductId) || '';
  }

  hideRecommendations(container) {
    const list = container.querySelector('[data-recommendations-list]');

    container.dataset.loadingProductId = '';
    container.classList.remove('is-ready');
    container.setAttribute('aria-hidden', 'true');
    if (list) list.innerHTML = '';
    window.clearInterval(this.recommendationsTimer);
  }

  renderRecommendation(product) {
    const title = this.escapeHTML(product.title || '');
    const url = this.escapeAttribute(product.url || '#');
    const image = this.getRecommendationImage(product);
    const priceValue = Number(product.price_min || product.price || 0);
    const compareAtPriceValue = this.getRecommendationCompareAtPrice(product, priceValue);
    const discountBadge = this.renderRecommendationBadge(product, priceValue, compareAtPriceValue);
    const imageMarkup = image
      ? `<img src="${this.escapeAttribute(image)}" alt="${title}" loading="lazy">`
      : '<div class="cart-drawer__recommendation-placeholder" aria-hidden="true"></div>';
    const compareAtPrice =
      compareAtPriceValue > priceValue
        ? `<s class="cart-drawer__recommendation-compare">${this.formatMoney(compareAtPriceValue)}</s>`
        : '';
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((item) => item.available) || variants[0];
    const addButton =
      variant && variant.id
        ? `<button type="button" class="cart-drawer__recommendation-add" data-recommendation-add data-variant-id="${this.escapeAttribute(variant.id)}" data-product-id="${this.escapeAttribute(product.id || '')}" aria-label="Add ${this.escapeAttribute(product.title || 'product')} to cart"><span aria-hidden="true">+</span></button>`
        : '';

    return `
      <article class="cart-drawer__recommendation" data-recommendation-item>
        <a href="${url}" class="cart-drawer__recommendation-link">
          <span class="cart-drawer__recommendation-media">${imageMarkup}${discountBadge}</span>
          <span class="cart-drawer__recommendation-info">
            <span class="cart-drawer__recommendation-title">${title}</span>
            <span class="cart-drawer__recommendation-prices">
              <span class="cart-drawer__recommendation-price">${this.formatMoney(priceValue)}</span>
              ${compareAtPrice}
            </span>
          </span>
        </a>
        ${addButton}
      </article>
    `;
  }

  getRecommendationCompareAtPrice(product, priceValue) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    let compareAtPrice = Number(product.compare_at_price_min || product.compare_at_price || 0);

    variants.forEach((variant) => {
      const variantPrice = Number(variant.price || priceValue || 0);
      const variantCompareAt = Number(variant.compare_at_price || 0);
      if (variantCompareAt > variantPrice && variantCompareAt > compareAtPrice) compareAtPrice = variantCompareAt;
    });

    return compareAtPrice;
  }

  renderRecommendationBadge(product, priceValue, compareAtPriceValue) {
    const container = this.querySelector('[data-cart-drawer-recommendations]');
    const mode = container ? container.dataset.saleBadgeMode || 'discount' : 'discount';
    const customText = container ? container.dataset.saleBadgeText || '' : '';
    const background = container ? container.dataset.saleBadgeBackground || '#EB4605' : '#EB4605';
    const textColor = container ? container.dataset.saleBadgeTextColor || '#ffffff' : '#ffffff';
    let label = '';
    let ariaLabel = '';

    if (mode === 'custom' && customText.trim()) {
      label = customText.trim();
      ariaLabel = label;
    } else if (compareAtPriceValue > priceValue) {
      const discountPercent = Math.round(((compareAtPriceValue - priceValue) * 100) / compareAtPriceValue);
      label = `-${discountPercent}%`;
      ariaLabel = `Save ${discountPercent} percent`;
    }

    if (!label) return '';

    return `<span class="product-discount-badge product-discount-badge--cart-recommendation" style="background-color: ${this.escapeAttribute(background)}; color: ${this.escapeAttribute(textColor)};" aria-label="${this.escapeAttribute(ariaLabel)}">${this.escapeHTML(label)}</span>`;
  }

  getRecommendationImage(product) {
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && product.featured_image.url) return product.featured_image.url;
    if (Array.isArray(product.images) && product.images[0]) return product.images[0];
    return '';
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);

    const amount = Number(cents) / 100;
    const currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  addRecommendationToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({
      id: Number(variantId),
      quantity: 1,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message);

        this.productId = button.dataset.productId || response.product_id || this.productId;

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-recommendations',
            productVariantId: variantId,
            cartData: response,
          });
        }

        this.renderContents(response);
      })
      .catch((error) => {
        console.error(error);
        button.disabled = false;
        button.classList.remove('is-loading');
      });
  }

  setupRecommendationsSlider() {
    const container = this.querySelector('[data-cart-drawer-recommendations]');
    const list = container ? container.querySelector('[data-recommendations-list]') : null;
    const items = list ? Array.from(list.querySelectorAll('[data-recommendation-item]')) : [];

    window.clearInterval(this.recommendationsTimer);
    if (!container || !list || !items.length) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    this.recommendationIndex = Math.min(this.recommendationIndex, items.length - 1);
    this.showRecommendationSlide(this.recommendationIndex);

    if (isMobile && items.length > 1) {
      this.recommendationsTimer = window.setInterval(() => this.moveRecommendationSlide(1), 3000);
    }
  }

  moveRecommendationSlide(direction) {
    const list = this.querySelector('[data-recommendations-list]');
    const items = list ? Array.from(list.querySelectorAll('[data-recommendation-item]')) : [];
    if (!items.length) return;

    this.recommendationIndex = (this.recommendationIndex + direction + items.length) % items.length;
    this.showRecommendationSlide(this.recommendationIndex);
    this.setupRecommendationsSlider();
  }

  showRecommendationSlide(index) {
    const list = this.querySelector('[data-recommendations-list]');
    const items = list ? Array.from(list.querySelectorAll('[data-recommendation-item]')) : [];
    if (!list || !items.length) return;

    list.style.setProperty('--recommendation-index', index);
    items.forEach((item, itemIndex) => {
      item.classList.toggle('is-active', itemIndex === index);
    });
  }

  setupUtilityPanels() {
    this.setupShippingEstimator();
  }

  toggleUtilityPanel(panelName, trigger) {
    const panel = this.querySelector(`[data-cart-utility-panel="${panelName}"]`);
    if (!panel) return;

    const panelOrder = ['discount', 'shipping'];
    const currentPanel = this.querySelector('[data-cart-utility-panel].is-open');
    const currentPanelName = currentPanel ? currentPanel.dataset.cartUtilityPanel : this.activeUtilityPanelName;
    const currentIndex = Math.max(panelOrder.indexOf(currentPanelName), 0);
    const nextIndex = Math.max(panelOrder.indexOf(panelName), 0);
    const direction = nextIndex >= currentIndex ? 'right' : 'left';
    const shouldOpen = panel.hidden || !panel.classList.contains('is-open');

    if (!shouldOpen) {
      this.closeUtilityPanels(direction);
      this.activeUtilityPanelName = '';
      return;
    }

    this.closeUtilityPanels(direction, panel);

    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    panel.classList.remove('is-leaving-left', 'is-leaving-right', 'is-entering-left', 'is-entering-right');
    panel.classList.add(direction === 'right' ? 'is-entering-right' : 'is-entering-left');
    window.requestAnimationFrame(() => {
      panel.classList.add('is-open');
      panel.classList.remove('is-entering-left', 'is-entering-right');
    });
    this.activeUtilityPanelName = panelName;

    const input = panel.querySelector('input');
    if (input) window.setTimeout(() => input.focus(), 120);
  }

  closeUtilityPanels(direction = 'right', exceptPanel = null) {
    this.querySelectorAll('[data-cart-utility-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.querySelectorAll('[data-cart-utility-panel]').forEach((panel) => {
      if (panel === exceptPanel) return;
      if (panel.hidden && !panel.classList.contains('is-open')) return;

      panel.classList.remove('is-open');
      panel.classList.remove('is-entering-left', 'is-entering-right');
      panel.classList.add(direction === 'right' ? 'is-leaving-left' : 'is-leaving-right');
      window.setTimeout(() => {
        if (!panel.classList.contains('is-open')) {
          panel.hidden = true;
          panel.classList.remove('is-leaving-left', 'is-leaving-right');
        }
      }, 240);
    });
  }

  applyDiscountFromField() {
    const input = this.querySelector('[data-cart-discount-field]');
    const code = input ? input.value : '';
    this.applyDiscountCode(code);
  }

  applyDiscountCode(code, button) {
    const discountCode = (code || '').trim();
    if (!discountCode) {
      this.setDiscountMessage('Enter a discount code.');
      return;
    }

    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }

    this.ensureCheckoutDiscountInput(discountCode);
    try {
      window.localStorage.setItem('soillar-cart-discount-code', discountCode);
    } catch (error) {}

    this.setDiscountMessage('Applying discount...');

    fetch(`/discount/${encodeURIComponent(discountCode)}?redirect=/cart`, {
      credentials: 'same-origin',
    })
      .then(() => this.refreshCartDrawerAfterDiscount(discountCode))
      .then(() => {
        this.ensureCheckoutDiscountInput(discountCode);
        this.setDiscountMessage(`${discountCode} applied.`);
      })
      .catch(() => {
        this.ensureCheckoutDiscountInput(discountCode);
        this.setDiscountMessage(`${discountCode} will be applied at checkout.`);
      })
      .finally(() => {
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      });
  }

  refreshCartDrawerAfterDiscount(discountCode) {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`, {
      credentials: 'same-origin',
    })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceInner = html.querySelector('.drawer__inner');
        const targetInner = this.querySelector('.drawer__inner');
        if (sourceInner && targetInner) targetInner.innerHTML = sourceInner.innerHTML;

        this.classList.remove('is-empty');
        this.classList.add('animate', 'active');
        this.ensureCheckoutDiscountInput(discountCode);
        this.initCartDrawerAddons();
      })
      .then(() => this.refreshMainCartSections());
  }

  refreshMainCartSections() {
    const mainItems = document.getElementById('main-cart-items');
    const mainFooter = document.getElementById('main-cart-footer');
    const tasks = [];

    if (mainItems) {
      tasks.push(
        fetch(`${routes.cart_url}?section_id=main-cart-items`, { credentials: 'same-origin' })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const source = html.querySelector('#main-cart-items .js-contents');
            const target = mainItems.querySelector('.js-contents');
            if (source && target) target.innerHTML = source.innerHTML;
          })
      );
    }

    if (mainFooter) {
      tasks.push(
        fetch(`${routes.cart_url}?section_id=main-cart-footer`, { credentials: 'same-origin' })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const source = html.querySelector('#main-cart-footer .js-contents');
            const target = mainFooter.querySelector('.js-contents');
            if (source && target) target.innerHTML = source.innerHTML;
          })
      );
    }

    if (!tasks.length) return Promise.resolve();
    return Promise.all(tasks).then(() => undefined);
  }

  setDiscountMessage(message) {
    const messageNode = this.querySelector('[data-cart-discount-message]');
    if (messageNode) messageNode.textContent = message;
  }

  applySavedDiscountToCheckoutForms() {
    let savedCode = '';

    try {
      savedCode = window.localStorage.getItem('soillar-cart-discount-code') || '';
    } catch (error) {}

    if (savedCode) this.ensureCheckoutDiscountInput(savedCode);
  }

  ensureCheckoutDiscountInput(code) {
    const forms = new Set(document.querySelectorAll('#CartDrawer-Form, #cart, form[action$="/cart"]'));
    forms.forEach((form) => {
      let input = form.querySelector('[data-cart-discount-input]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'discount';
        input.setAttribute('data-cart-discount-input', '');
        form.appendChild(input);
      }
      input.value = code;
    });
  }

  setupShippingEstimator() {
    const input = this.querySelector('[data-cart-shipping-zip]');
    if (!input) return;

    const savedZip = this.getSavedDeliveryZip();
    if (savedZip) {
      input.value = savedZip;
      this.renderShippingEstimate(savedZip, false);
    } else {
      this.renderShippingEmpty();
    }
  }

  getShippingInputValue() {
    const input = this.querySelector('[data-cart-shipping-zip]');
    return input ? input.value : '';
  }

  getSavedDeliveryZip() {
    try {
      const storageKeys = this.getDeliveryStorageKeys();
      for (const key of storageKeys) {
        const zip = this.cleanZip(window.localStorage.getItem(key) || '');
        if (zip.length === 5) return zip;
      }

      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);
        if (!key || key.indexOf('soillar_delivery_zip_') !== 0) continue;
        const zip = this.cleanZip(window.localStorage.getItem(key) || '');
        if (zip.length === 5) return zip;
      }
    } catch (error) {}

    return '';
  }

  getDeliveryStorageKeys() {
    const firstCartItem = this.querySelector('[data-cart-product-id]');
    const productId = firstCartItem ? firstCartItem.dataset.cartProductId : '';
    const keys = ['soillar_delivery_zip_cart'];
    if (productId) keys.unshift(`soillar_delivery_zip_${productId}`);
    return keys;
  }

  saveDeliveryZip(zip) {
    try {
      this.getDeliveryStorageKeys().forEach((key) => window.localStorage.setItem(key, zip));
    } catch (error) {}
  }

  renderShippingEmpty() {
    const result = this.querySelector('[data-cart-shipping-result]');
    if (result) result.textContent = 'Enter ZIP code to estimate delivery.';
  }

  renderShippingEstimate(value, shouldSave) {
    const input = this.querySelector('[data-cart-shipping-zip]');
    const result = this.querySelector('[data-cart-shipping-result]');
    if (!result) return;

    const zip = this.cleanZip(value);
    if (input) input.value = zip;

    if (zip.length !== 5) {
      this.renderShippingEmpty();
      return;
    }

    const state = this.stateFromZip(zip);
    if (!state) {
      result.textContent = 'Invalid ZIP code.';
      return;
    }

    const days = this.deliveryDaysForState(state);
    result.innerHTML = `Estimated delivery between <strong>${this.deliveryDate(days[0])}</strong> and <strong>${this.deliveryDate(days[1])}</strong>.`;

    if (shouldSave) this.saveDeliveryZip(zip);
  }

  cleanZip(value) {
    return (value || '').replace(/\D/g, '').slice(0, 5);
  }

  stateFromZip(zip) {
    const code = parseInt(zip, 10);
    if (Number.isNaN(code)) return '';

    const zipStateRanges = [
      [35000, 36999, 'AL'], [99500, 99999, 'AK'], [85000, 86999, 'AZ'],
      [71600, 72999, 'AR'], [90000, 96699, 'CA'], [80000, 81999, 'CO'],
      [6000, 6999, 'CT'], [19700, 19999, 'DE'], [32000, 34999, 'FL'],
      [30000, 31999, 'GA'], [39800, 39999, 'GA'], [96700, 96899, 'HI'],
      [83200, 83999, 'ID'], [60000, 62999, 'IL'], [46000, 47999, 'IN'],
      [50000, 52999, 'IA'], [66000, 67999, 'KS'], [40000, 42799, 'KY'],
      [70000, 71599, 'LA'], [3900, 4999, 'ME'], [20600, 21999, 'MD'],
      [1000, 2799, 'MA'], [48000, 49999, 'MI'], [55000, 56799, 'MN'],
      [38600, 39799, 'MS'], [63000, 65999, 'MO'], [59000, 59999, 'MT'],
      [68000, 69999, 'NE'], [88900, 89999, 'NV'], [3000, 3899, 'NH'],
      [7000, 8999, 'NJ'], [87000, 88499, 'NM'], [10000, 14999, 'NY'],
      [27000, 28999, 'NC'], [58000, 58999, 'ND'], [43000, 45999, 'OH'],
      [73000, 74999, 'OK'], [97000, 97999, 'OR'], [15000, 19699, 'PA'],
      [300, 999, 'PR'], [2800, 2999, 'RI'], [29000, 29999, 'SC'],
      [57000, 57999, 'SD'], [37000, 38599, 'TN'], [75000, 79999, 'TX'],
      [88500, 88599, 'TX'], [84000, 84999, 'UT'], [5000, 5999, 'VT'],
      [20100, 20599, 'VA'], [22000, 24699, 'VA'], [20000, 20099, 'DC'],
      [98000, 99499, 'WA'], [24700, 26999, 'WV'], [53000, 54999, 'WI'],
      [82000, 83199, 'WY'],
    ];

    for (const range of zipStateRanges) {
      if (code >= range[0] && code <= range[1]) return range[2];
    }

    return '';
  }

  deliveryDaysForState(state) {
    const warehouseStates = ['CA', 'GA', 'NJ'];
    const neighboringStates = {
      CA: ['OR', 'NV', 'AZ'],
      GA: ['FL', 'AL', 'TN', 'SC', 'NC'],
      NJ: ['NY', 'PA', 'DE', 'MD', 'CT'],
    };

    if (warehouseStates.indexOf(state) !== -1) return [2, 3];

    for (const warehouseState of warehouseStates) {
      if ((neighboringStates[warehouseState] || []).indexOf(state) !== -1) return [3, 4];
    }

    return [4, 5];
  }

  deliveryDate(daysFromToday) {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + daysFromToday);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  hideExternalSupportButtons() {
    const selectors = [
      '#ShopifyChat',
      '#shopify-chat',
      '[id*="ShopifyChat"]',
      '[id*="shopify-chat"]',
      '[class*="shopify-chat"]',
      '[class*="shopify-inbox"]',
      '[data-testid*="chat"]',
      '[data-testid*="support"]',
      'iframe[title*="chat" i]',
      'iframe[title*="support" i]',
      'iframe[src*="shopify" i][src*="chat" i]',
      'iframe[src*="inbox" i]',
    ];

    document.querySelectorAll(selectors.join(',')).forEach((element) => {
      if (this.contains(element) || element.closest('cart-drawer')) return;
      if (element.dataset.soillarCartHiddenSupport === 'true') return;

      element.dataset.soillarCartHiddenSupport = 'true';
      element.dataset.soillarCartPreviousVisibility = element.style.visibility || '';
      element.dataset.soillarCartPreviousOpacity = element.style.opacity || '';
      element.dataset.soillarCartPreviousPointerEvents = element.style.pointerEvents || '';
      element.style.setProperty('visibility', 'hidden', 'important');
      element.style.setProperty('opacity', '0', 'important');
      element.style.setProperty('pointer-events', 'none', 'important');
    });
  }

  showExternalSupportButtons() {
    document.querySelectorAll('[data-soillar-cart-hidden-support="true"]').forEach((element) => {
      element.style.visibility = element.dataset.soillarCartPreviousVisibility || '';
      element.style.opacity = element.dataset.soillarCartPreviousOpacity || '';
      element.style.pointerEvents = element.dataset.soillarCartPreviousPointerEvents || '';
      delete element.dataset.soillarCartHiddenSupport;
      delete element.dataset.soillarCartPreviousVisibility;
      delete element.dataset.soillarCartPreviousOpacity;
      delete element.dataset.soillarCartPreviousPointerEvents;
    });
  }

  escapeHTML(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  escapeAttribute(value) {
    return this.escapeHTML(value);
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
